import { useCallback, useEffect, useMemo, useState } from 'react';
import { convertCoordinates } from 'api/converter';
import { DEFAULT_CONVERTER_HEIGHT } from 'constants/converter';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useMap, useMapRef } from 'hooks/map';
import { ReactComponent as Chevron } from 'images/newIcons/chevron.svg';
import { ReactComponent as Copy } from 'images/newIcons/copy.svg';
import mapboxgl from 'mapbox-gl';
import { settingsActions } from 'store/slices/mapV2/mapReducer/settingsSlice';
import { coordsModeSelector } from 'store/slices/mapV2/mapReducer/settingsSlice/selectors';
import { CoordinateSystems } from 'types';
import { useDebouncedCallback } from 'use-debounce';

import { ScaleBar } from 'components/Map/ScaleBar';
import ClosableTooltip from 'components/ui/ClosableTooltip';
import { IconButton } from 'components/ui/IconButton';
import {
  copyToClipboard,
  formatWgsCoords,
  formatXYCoords,
  formatXYCoordsShortSquare,
  getVisibleCoordMode,
  getZoomPercent,
} from 'utils';

import { CoordsModeTooltipOverlay } from './CoordsModeTooltipOverlay';
import { ScaleControls } from './ScaleControls';

const ZOOM_CHANGE_STEP = 1;

export const MapFooter = () => {
  const mapContext = useMap();
  const { mapRef } = useMapRef();
  const coordsMode = useAppSelector(coordsModeSelector);
  const [zoom, setZoom] = useState('0');
  const [coordinateSquare, setCoordinateSquare] = useState('');
  const [formattedCoords, setFormattedCoords] = useState('0.000, 0.000');
  const [wgsCoords, setWgsCoords] = useState(new mapboxgl.LngLat(0, 0));

  const dispatch = useAppDispatch();

  const [minZoom, maxZoom] = useMemo(
    () => [mapRef.current?.getMinZoom(), mapRef.current?.getMaxZoom()],
    [mapRef]
  );

  const visibleCoordsMode = getVisibleCoordMode(coordsMode);

  const setCoords = (lat: number, lng: number) => {
    setFormattedCoords(formatWgsCoords(lat, lng));
    dispatch(
      settingsActions.setCenterCoords({
        b: lat,
        l: lng,
        h: DEFAULT_CONVERTER_HEIGHT,
      })
    );
  };

  const setConvertedCoords = async (
    lat: number,
    lng: number,
    targetCrs: CoordinateSystems.SK42 | CoordinateSystems.USK2000
  ) => {
    const data = await convertCoordinates(CoordinateSystems.WGS, targetCrs, {
      b: lat,
      l: lng,
      h: DEFAULT_CONVERTER_HEIGHT,
    });

    if (data.to === targetCrs) {
      const { x, y } = data.payload;

      setCoordinateSquare(formatXYCoordsShortSquare(x, y));
      setFormattedCoords(formatXYCoords(x, y));

      dispatch(settingsActions.setCenterCoords(data.payload));
    }
  };

  const debouncedSetCoords = useDebouncedCallback(setCoords, 20);

  const debouncedSetConvertedCoords = useDebouncedCallback(
    setConvertedCoords,
    20
  );

  const handleZoomUpdate = (zoomProcessor: (currentZoom: number) => number) => {
    mapRef.current?.flyTo({
      zoom: zoomProcessor(mapRef.current?.getZoom()),
      center: mapRef.current?.getCenter(),
    });
  };

  const handleZoomDecrease = () =>
    handleZoomUpdate((zoomLvl) => zoomLvl - ZOOM_CHANGE_STEP);

  const handleZoomIncrease = () =>
    handleZoomUpdate((zoomLvl) => zoomLvl + ZOOM_CHANGE_STEP);

  const updateZoom = (map: mapboxgl.Map) => {
    const zoomPercentage = getZoomPercent(
      map.getZoom(),
      minZoom,
      maxZoom
    ).toFixed();

    setZoom(zoomPercentage);
  };

  const updateCoords = (map: mapboxgl.Map) => setWgsCoords(map.getCenter());

  const debouncedUpdater = useDebouncedCallback((map) => {
    updateZoom(map);
    updateCoords(map);
  }, 5);

  const handleCopyCoords = useCallback(
    () => copyToClipboard(formattedCoords),
    [formattedCoords]
  );

  useEffect(() => {
    mapRef.current && debouncedUpdater(mapRef.current);
  }, [mapContext]);

  useEffect(() => {
    if (coordsMode === CoordinateSystems.WGS) {
      debouncedSetCoords(wgsCoords.lat, wgsCoords.lng);
    } else if (
      coordsMode === CoordinateSystems.SK42 ||
      coordsMode === CoordinateSystems.USK2000
    ) {
      debouncedSetConvertedCoords(wgsCoords.lat, wgsCoords.lng, coordsMode);
    }
  }, [coordsMode, wgsCoords]);

  return (
    <footer className="flex flex-row justify-between items-center h-9 px-4 py-2 bg-dark tpg-c2 text-tpg_base">
      <div className="flex flex-1 items-center gap-4">
        <ScaleControls
          scale={zoom}
          decreaseScale={handleZoomDecrease}
          increaseScale={handleZoomIncrease}
        />
        <ScaleBar
          zoom={mapContext.viewport.zoom}
          latitude={mapContext.viewport.latitude}
        />
      </div>
      {coordsMode === CoordinateSystems.SK42 && coordinateSquare && (
        <span className="flex flex-1 justify-center tpg-input text-tpg_title">
          Квадрат {coordinateSquare}
        </span>
      )}
      <div className="flex flex-1 justify-end items-center gap-2">
        <ClosableTooltip overlay={<CoordsModeTooltipOverlay />} trigger="click">
          <div
            role="button"
            className="flex items-center tpg-b1 cursor-pointer"
          >
            {visibleCoordsMode}
            <Chevron />
          </div>
        </ClosableTooltip>
        <div className="flex items-center">
          <span className="tpg-input text-white">{formattedCoords}</span>
        </div>
        <IconButton
          icon={<Copy />}
          classNames={{
            container: '!text-tpg_light hover:!text-bright_product',
          }}
          onClick={handleCopyCoords}
        />
      </div>
    </footer>
  );
};
